import ReactFlow, { Background, Controls, ReactFlowProvider, addEdge, useReactFlow } from 'reactflow'
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react'

import 'reactflow/dist/style.css'
import { ConnectionLine } from '../connectionline'
import { WorkflowEdge } from '../../workflow2.0/edges'
import { useTheme } from '@emotion/react'
import TechnologyNode from '../nodes/TechnologyNode'
import StartNode from '../nodes/StartNode'
import EndNode from '../nodes/EndNode'
import { getRandomUUID } from '../../../generic/utilities/randomuuid'

/**
 * Smart ReactFlow component
 * @param {Object} props Props object
 * @param {import('reactflow').Node[]} props.nodes Nodes array
 * @param {React.Dispatch<React.SetStateAction<Node<any, string | undefined>[]>>} props.setNodes Set nodes function
 * @param {import('reactflow').Node} props.newNode New node object
 * @param {import('reactflow').Edge[]} props.edges Edges array
 * @param {React.Dispatch<React.SetStateAction<Node<any, string | undefined>[]>>} props.setEdges Set edges function
 * @param {import('reactflow').OnNodesChange} props.onNodesChange On nodes change function
 * @param {import('reactflow').OnEdgesChange} props.onEdgesChange On edges change function
 * @param {import('reactflow').Viewport} props.defaultViewport Default viewport object
 * @param {Boolean} props.showWorkflowTable Show workflow table
 * @param {Boolean} props.fitView Fit view
 * @param {Number} props.technologyNum Technology number
 * @param {function(number)} props.technologyNumChange Technology number change function
 * @param {Boolean} props.isNodeSelected Is node selected
 * @param {function(import('reactflow').Node[])} props.onNodeSelectionChange Node selection change function
 * @param {function(import('reactflow').Node[])} props.onEdgeSelectionChange Edge selection change function
 * @param {Boolean} props.disableFitOnResize Disable fit on resize
 * @param {Object} props.context Context object
 * @param {Boolean} props.editMode Edit mode
 * @param {function(string)} props.onEdgeDeleteEvent Edge delete event function
 * @param {function(import('reactflow').Edge)} props.onAddEdgeEvent Edge add event function
 * @param {function(event, position)} props.onDrop On drop function
 * @param {function(import('reactflow').Edge)} props.onUpdateEdge Edge update function
 * @param {function(import('reactflow').Node)} props.onNodeIsFirst Node is first function
 * @returns
 */
const SmartReactflowInternal = ({
  nodes,
  setNodes,
  edges,
  setEdges,
  onNodesChange,
  onEdgesChange,
  defaultViewport,
  showWorkflowTable,
  fitView,
  onDrop = (event, position) => {},
  isNodeSelected = false,
  onNodeSelectionChange,
  onEdgeSelectionChange,
  disableFitOnResize = false,
  context = null,
  editMode = false,
  onEdgeDeleteEvent,
  onAddEdgeEvent,
  onUpdateEdge,
  onNodeDoubleClick,
  // Story 167: a library drag over the canvas says where it would go (no ghost is drawn, PO 29/09/2026).
  onCanvasDragOver = () => {},
  onCanvasDragLeave = () => {}
}) => {
  const reactFlowWrapper = useRef(null)
  const [connectionLineType, setConnectionLineType] = useState('default')
  const reactFlowInstance = useReactFlow()
  const theme = useTheme()

  const nodeTypes = useMemo(() => ({
    // subWorkflowNode: SubWorkflowNode,
    technologyNode: TechnologyNode,
    startNode: StartNode,
    endNode: EndNode
  }), [])

  const edgeTypes = useMemo(() => ({
    custom: (props) => <WorkflowEdge {...props} editing={editMode} />
  }), [editMode])

  const onConnect = useCallback((params) => {
    let exists = false
    edges.forEach(edge => {
      if (edge.source === params.source &&
          edge.sourceHandle === params.sourceHandle &&
          edge.target === params.target) {
        exists = true
      }
    })
    if (exists === true) {
      return
    }

    params.id = getRandomUUID()
    params.data = {
      nodeId: params.source,
      type: params.sourceHandle,
      selected: false,
      connectivity: 'FLEXIBLE',
      eventType: null,
      eventValue: null,
      edgeCardVisible: true,
      startDate: null,
      timezone: null,
      disableEdges: false,
      deleteEdge: onEdgeDeleteEvent,
      updateEdge: onUpdateEdge
    }
    params.type = 'custom'
    setEdges((eds) => addEdge(params, eds))
    onAddEdgeEvent(params)
  }, [setEdges, edges, onEdgeDeleteEvent, onAddEdgeEvent, onUpdateEdge])

  const onDragOver = useCallback((event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    onCanvasDragOver()
  }, [onCanvasDragOver])

  const onSelectionChange = (selection) => {
    let nodesChanges = false
    let edgeChanges = false
    const newNodeValues = nodes.map((node) => {
      if (!node.data.selected && selection.nodes.find((n) => n.id === node.id)) {
        node.data = { ...node.data }
        node.data.selected = true
        nodesChanges = true
      }

      if (node.data.selected && !selection.nodes.find((n) => n.id === node.id)) {
        node.data = { ...node.data }
        node.data.selected = false
        nodesChanges = true
      }
      return node
    })

    const newEdgeValues = edges.map((edge) => {
      if (!edge.data.selected && selection.edges.find((e) => e.id === edge.id)) {
        edge.data = { ...edge.data }
        edge.data.selected = true
        edgeChanges = true
      }

      if (edge.data.selected && !selection.edges.find((e) => e.id === edge.id)) {
        edge.data = { ...edge.data }
        edge.data.selected = false
        edgeChanges = true
      }
      return edge
    })

    if (nodesChanges) {
      setNodes(newNodeValues)
      if (onNodeSelectionChange && typeof onNodeSelectionChange === 'function') {
        onNodeSelectionChange(newNodeValues.filter((node) => node.data.selected))
      }
    }
    if (edgeChanges) {
      setEdges(newEdgeValues)
      if (onEdgeSelectionChange && typeof onEdgeSelectionChange === 'function') {
        onEdgeSelectionChange(newEdgeValues.filter((edge) => edge.data.selected))
      }
    }
  }

  const onWheel = useCallback((event) => {
    if (event.ctrlKey) {
      event.preventDefault() // Prevent normal scroll behavior when Alt is pressed
      // Determine zoom direction and update zoom level
      if (event.deltaY < 0) {
        reactFlowInstance.zoomIn()
      } else {
        reactFlowInstance.zoomOut()
      }
    }
  }, [reactFlowInstance])

  // Add the wheel event listener manually with { passive: false }
  useEffect(() => {
    const wrapper = reactFlowWrapper.current
    if (wrapper) {
      wrapper.addEventListener('wheel', onWheel, { passive: false })
    }
    // Clean up the event listener on component unmount
    return () => {
      if (wrapper) {
        wrapper.removeEventListener('wheel', onWheel)
      }
    }
  }, [onWheel])

  useEffect(() => {
    if (!reactFlowWrapper.current || disableFitOnResize) return
    const resizeObserver = new global.ResizeObserver(() => {
      // Do what you want to do when the size of the element changes
      reactFlowInstance?.fitView()
    })
    resizeObserver.observe(reactFlowWrapper.current)
    return () => resizeObserver.disconnect() // clean up
  }, [reactFlowInstance, disableFitOnResize])

  useEffect(() => {
    setTimeout(() => reactFlowInstance?.fitView(), 0)
  }, [reactFlowInstance, showWorkflowTable, fitView])

  return (
    <div
      ref={reactFlowWrapper}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          onCanvasDragLeave()
        }
      }}
    >
      <ReactFlow
        zoomOnScroll={false}
        preventScrolling={false}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDrop={(event) => {
          event.preventDefault()
          const position = reactFlowInstance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY
          })
          onDrop(event, position)
        }}
        onDragOver={onDragOver}
        fitView
        edgeTypes={edgeTypes}
        nodeTypes={nodeTypes}
        defaultViewport={defaultViewport}
        onConnectStart={(event, data) => {
          setConnectionLineType(data.handleId)
        }}
        style={{ backgroundColor: theme.palette.background.row2 }}
        proOptions={{ hideAttribution: true }}
        onSelectionChange={(selection) => onSelectionChange(selection)}
        deleteKeyCode='FakeDelete'
        connectionLineComponent={(event) => ConnectionLine({ fromX: event.fromX, fromY: event.fromY, toX: event.toX, toY: event.toY, label: event.label, type: connectionLineType, style: event.style })}
        panOnDrag
        onNodeDoubleClick={onNodeDoubleClick}
      >
        <Background />
        <Controls style={{ right: 1, left: 'auto' }} />
      </ReactFlow>
    </div>
  )
}

// Some methods need to be in the component inside ReactFlowProvider
// https://reactflow.dev/learn/troubleshooting#solution-for-b
const SmartReactflow = (props) => {
  return (
    <ReactFlowProvider>
      <SmartReactflowInternal {...props} />
    </ReactFlowProvider>
  )
}

export default SmartReactflow
